import maplibregl from 'maplibre-gl'

/**
 * Fit the given map instance to the extent of the provided GeoJSON data.
 *
 * @param map - The MapLibre GL map instance.
 * @param geojsonData - A GeoJSON FeatureCollection.
 * @param padding - Optional padding (default is 250).
 */
export function fitMapToGeojson({
  geojsonData,
  map,
}: {
  geojsonData: {
    features: Array<{
      geometry: { coordinates: [number, number]; type: 'Point' }
      properties: any
      type: 'Feature'
    }>
    type: 'FeatureCollection'
  }
  map: maplibregl.Map | null
}) {
  if (!map || geojsonData.features.length === 0) {
    return
  }

  const firstFeature = geojsonData.features[0]

  // If there are no features, don't try to fit the map
  if (!firstFeature) {
    return
  }

  const [firstLng, firstLat] = firstFeature.geometry.coordinates

  // If the first feature has no coordinates, bail out
  if (!firstLng || !firstLat) {
    return
  }

  const initialLngLat = new maplibregl.LngLat(firstLng, firstLat)
  const bounds = new maplibregl.LngLatBounds(initialLngLat, initialLngLat)

  geojsonData.features.forEach((feature) => {
    const [lng, lat] = feature.geometry.coordinates
    if (typeof lng === 'number' && typeof lat === 'number') {
      bounds.extend(new maplibregl.LngLat(lng, lat))
    }
  })

  // Get the original bounds
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  const center = bounds.getCenter()

  // This shrinks the bounds toward the center
  const shrinkFactor = 0.8
  const lngHalfWidth = ((ne.lng - sw.lng) * shrinkFactor) / 2
  const latHalfHeight = ((ne.lat - sw.lat) * shrinkFactor) / 2

  const reducedBounds = new maplibregl.LngLatBounds(
    new maplibregl.LngLat(
      center.lng - lngHalfWidth,
      center.lat - latHalfHeight,
    ),
    new maplibregl.LngLat(
      center.lng + lngHalfWidth,
      center.lat + latHalfHeight,
    ),
  )

  // Use the reduced bounds for fitting
  const reducedSw = reducedBounds.getSouthWest()
  const reducedNe = reducedBounds.getNorthEast()

  map.fitBounds(
    [
      [reducedSw.lng, reducedSw.lat],
      [reducedNe.lng, reducedNe.lat],
    ],
    { maxZoom: 9 },
  )
}
